import { BaseApiResponse } from './api/api-v2';
import { KarlaOrder } from './api/karla';
import { SkuOrderStatus } from './OrderStatusType';
import { AskMarketValue, BidMarketValue } from './utilities';

export type SkuDTO = {
  id: string;
  slug: string;
  created: string;
  name: string;
  mainImageUrl: string;
  description: string;
  totalViews?: number;
  totalTrades: number;
  totalSaves: number;
  lastSellValue: number;
  lowestAsk: number;
  retailPrice: number;
  releaseDate: string;
  wbid?: string;
  asks: Ask[];
  bids: Bid[];
  orders: Order[];
  offer: Offer[];
  notification: Notification[];
  wishlist: Wishlist[];
  skuDetailViewed: SkuDetailViewed[];
  assets: UserAsset[];
  dayBottle?: DayBottle;
  bottleSizeInMl?: number;
  volumeInPercent?: number;
  distilledYear?: string;
  numberOfBottles?: number;
  brand?: Brand;
  category?: Category;
  subCategory?: string;
  spiritoryBottle: boolean;
};

export type Category = {
  id: string;
  name: string;
  publishedAt: string | null;
};

export type Brand = {
  id: string;
  name: string;
  region: string;
  subRegion: string | null;
  countryOfOrigin: string;
  image: string | null;
  description: string | null;
  publishedAt: string;
};

export type UserAsset = {
  id: number;
  skuId: number;
  slug: string;
  quantity: number;
  purchasePrice: number;
  marketValue: number;
  purchaseDate: Date;
  profitLoss: number;
  ownerId: number;
};

export type Wishlist = {
  slug: string;
  userId?: string;
  added: Date;
};

export enum AsksStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  TIMED_OUT = 'TIMED_OUT',
  FULFILLED = 'FULFILLED',
  CANCELLED = 'CANCELLED',
  FULFILLED_EXTERNAL = 'FULFILLED_EXTERNAL',
  MATCHED_CANCELLED = 'MATCHED_CANCELLED',
  USER_DISABLED = 'USER_DISABLED',
  PAUSED = 'PAUSED',
}

export enum BillStatus {
  UNPAID = 'UNPAID',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export enum BillType {
  PAYER = 'PAYER',
  PAYEE = 'PAYEE',
}

export type DayBottle = {
  id: number;
  skuSlug: string;
};

export type ShippingPrice = {
  id: number;
  eu: number;
  inland: number;
  ownerId: number;
};

export enum BidStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  TIMED_OUT = 'TIMED_OUT',
  FULFILLED = 'FULFILLED',
  CANCELLED = 'CANCELLED',
  FULFILLED_EXTERNAL = 'FULFILLED_EXTERNAL',
  MATCHED_CANCELLED = 'MATCHED_CANCELLED',
  USER_DISABLED = 'USER_DISABLED',
  PAUSED = 'PAUSED',
}

export type PayeeFees = {
  billId: number;
  amount: number;
  feePercent: number;
  net: number;
  paymentFeePercent: number;
  paymentProcessing: number;
  processingFee: number;
  salesTaxPercent: number;
  totalPayout: number;
  vat: number;
  voucherValue: number;
  shippingPrice?: number;
};

export type PayerFees = {
  billId: number;
  amount: number;
  feePercent: number;
  net: number;
  packagingFeeFixed: number;
  originalTotalPayment: number | null;
  paymentFeePercent: number;
  processingFee: number;
  salesTaxPercent: number;
  totalPayment: number;
  vat: number;
  voucherValue: number;
  shippingPrice?: number;
};

export type Bill = {
  id: number;
  askValue: number;
  billNumber: string;
  bidValue: number;
  payValue: number;
  created: Date;
  status: BillStatus;
  type: BillType;
  order?: Order;
  offer?: Offer;
  payeeFees?: PayeeFees;
  payerFees?: PayerFees;
  owner?: User;
};

export type OfferAuthImages = {
  id: number;
  offerAuthId: number;
  frontLabel: string;
  backLabel: string;
  fillingLevel: string;
  bottleNeck: string;
  seal: string;
  bottle?: string;
  outerBoxFront?: string;
  outerBoxBack?: string;
  bottleNeckBack?: string;
};

export type OfferAuthImagesAdmin = {
  name: string;
  label: string;
  value: string;
  required: boolean;
};

export type SkuDetailViewed = {
  skuId: number;
  userId: number;
  created: Date;
};

export type Notification = {
  id: number;
  description: string;
  created: string;
  category: NotificationCategory;
  status: NotificationStatus;
  skuId: number;
  userId: number;
};

export type OfferAuth = {
  id: number;
  offerNumber: string;
  ownerUserId: number;
  created: Date;
  submitted: Date;
  softRejected: Date | null;
  softRejectedReason: string | null;
  rejected: Date;
  rejectedReason?: string;
  offer: Offer;
  images?: OfferAuthImages;
};

export type OfferAuthDto = {
  created: Date;
  submitted: Date;
  rejected: Date;
};

export type PaymentIntent = {
  id: number;
  orderId: number;
  intentId: string;
  intentType: string;
  hostedUrl: string | undefined;
  created: Date;
  status: string;
  clientSecret: string;
};

export type ShippingPolicy = {
  id: number;
  package: string;
  carrier: string;
  custom: boolean;
  domestic: number;
  europe: number;
  worldwide?: number;
  AT?: number;
  BE?: number;
  BG?: number;
  HR?: number;
  CY?: number;
  CZ?: number;
  DK?: number;
  EE?: number;
  FI?: number;
  FR?: number;
  DE?: number;
  GR?: number;
  HU?: number;
  IE?: number;
  IT?: number;
  LV?: number;
  LT?: number;
  LU?: number;
  NL?: number;
  PL?: number;
  PT?: number;
  RO?: number;
  SK?: number;
  SI?: number;
  ES?: number;
  SE?: number;
  GB?: number;
  ownerId: number;
};

export type ShippingAddress = {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  line1: string;
  postalCode: string;
  countryCode: string;
  city: string;
};

export type User = {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  dob?: Date;
  provider: string;
  providerId: string;
  created: Date;
  emailVerified: boolean;
  verificationLink?: string;
  disabled: boolean;
  emailsOn: boolean;
  lastRefreshTime: Date;
  lastSignIn: Date;
  feeId: number;
  canAsk: boolean;
  canBid: boolean;
  isAdmin: boolean;
  stripeCustomerId?: string;
  accountNumber?: string;
  stripeAccountId?: string;
  wishlist: Wishlist[];
  skuDetailViewed: SkuDetailViewed[];
  local: string;
  asks: Ask[];
  bids: Bid[];
  offer: Offer[];
  order: Order[];
  userAddress?: UserAddress;
  bill: Bill[];
  vouchers: Voucher[];
  assets: UserAsset[];
  shippingPrice?: ShippingPrice;
  shippingPolicy?: ShippingPolicy;
  shippingAddress?: ShippingAddress;
  AllowedCountries?: string[] | null;
};

export type UserAddress = {
  id: number;
  userId: number;
  line1: string;
  postalCode: string;
  countryCode: string;
  city: string;
};

export type Fee = {
  id: number;
  name: string;
  buyerCommissionFeePercent: number;
  buyerShippingFeeFixed: number;
  created: Date;
  salesTaxPercent: number;
  sellerCommissionFeePercent: number;
  sellerPaymentProcessingFeePercent: number;
};

export type Voucher = {
  id: number;
  category: number;
  minBidAskValue: number;
  created: Date;
  validUntil: Date;
  value: number;
  ownerUserId?: number;
  orderId?: number;
  offerId?: number;
};

export type Offer = {
  id: number;
  offerNumber: string;
  askId: number;
  bidId: number;
  skuId: number;
  ownerUserId: number;
  billId: number;
  askUserStripeAccountId: string;
  created: Date;
  payoutUntil: Date;
  payValue: number;
  ask: Ask;
  bid: Bid;
  owner: User;
  skuOrderStatus?: SkuOrderStatus;
  offerAuth?: OfferAuth;
  sku: SkuDTO;
  bill?: Bill;
  invoice?: {
    invoiceUrl?: string;
    receiptUrl?: string;
    sellerBuyerInvoiceUrl?: string;
  };
};

export type Ask = {
  id: number;
  value: number;
  status: AsksStatus;
  created: Date;
  payValue: number;
  skuId: number;
  validUntil: Date;
  index: Date;
  fulfilledDate: Date;
  skipAuth: boolean;
  ownerUserId: number;
  offer?: Offer;
  order?: Order;
  sku?: SkuDTO;
  isSellerProfessional?: boolean;
};

export type Bid = {
  id: number;
  value: number;
  status: BidStatus;
  created: string;
  payUntil: Date;
  skuId: number;
  validUntil: string;
  ownerUserId: number | string;
  index: Date;
  offer?: Offer;
  order?: Order;
  sku?: SkuDTO;
};

export type BidDTO = {
  id: number;
  value: number;
  status: BidStatus;
  created: string;
  payUntil: string;
  skuId: number;
  validUntil: Date;
  ownerUserId: string;
};

export type Order = {
  payValue: number;
  created: string;
  id: number;
  orderNumber: string;
  billId: number;
  payUntil: Date;
  receiveUntil: Date;
  ask: Ask;
  bid: Bid;
  sku: SkuDTO;
  skuOrderStatus?: SkuOrderStatus;
  paymentIntents: PaymentIntent[];
  bill?: Bill;
  offer?: Offer;
  owner?: User;
  invoice?: {
    invoiceUrl?: string;
    receiptUrl?: string;
    sellerBuyerInvoiceUrl?: string;
  };
  karlaOrder: KarlaOrder | null;
};

export interface skuDataProp {
  id: string;
  asks?: Ask[];
  bids?: Bid[];
  order?: Order[];
  slug?: string;
}

export enum NotificationCategory {
  BID_STATUS = 'BID_STATUS',
  ASK_STATUS = 'ASK_STATUS',
  ORDER_STATUS = 'ORDER_STATUS',
  OFFER_STATUS = 'OFFER_STATUS',
  SHIPPING_STATUS = 'SHIPPING_STATUS',
  AUTHENTICATION_STATUS = 'AUTHENTICATION_STATUS',
}

export enum NotificationStatus {
  READ = 'READ',
  NOT_READ = 'NOT_READ',
}

export type ExternalOrder = {
  id: number;
  waName: string;
  value: number;
  created: string;
};

export type SkuStrapi = {
  name: string;
  slug: string;
  releaseDate: string;
  mainImageUrl: string;
  subCategory: string;
  updatedAt: string;
  brand: {
    id: number;
    name: string;
    region: string;
    countryOfOrigin: string;
    publishedAt: string;
    description: string;
    image: string;
  };
  category: {
    id: number;
    name: string;
    publishedAt: string;
  };
  totalViews?: number;
};

export type SkuPageProp = {
  id: string;
  slug: string;
  name: string | null;
  mainImageUrl: string | null;
  bottleSizeInMl: number | null;
  waName: string | null;
  lowestAsk: number | null;
  retailPrice: number;
  distilledYear: string;
  releaseDate: string;
  volumeInPercent: number | null;
  numberOfBottles: number | null;
  brand: Brand | null;
  category: Category | null;
  asks: Array<AskMarketValue>;
  bids: Array<BidMarketValue>;
  description: string | null;
  userRequestingId: string | null;
};

export type SkuAllOrders = BaseApiResponse<
  {
    payValue: number;
    skuOrderStatus: {
      paid: string;
    };
  }[]
>;

export type AdminDisplayOrder = {
  id: string;
  payValue: number;
  orderId: string;
  externalOrderId: null;
  skuId: string;
  displayStatus: 'ACTIVE' | 'INACTIVE';
  payDate: string;
  createdAt: string;
  updatedAt: string;
};

export type SkuPageDataSku = {
  id: string;
  name: string;
  slug: string;
  category: {
    id: string;
    name: string;
    publishedAt: string;
  };
  brand: {
    id: string;
    name: string;
    region: string;
    subRegion: string;
    countryOfOrigin: string;
    image: string;
    description: string;
    publishedAt: string;
  };
  bottleSizeInMl: number;
  volumeInPercent: number;
  releaseDate: string;
  distilledYear: string;
  numberOfBottles: number;
  description: any;
  mainImageUrl: string;
  retailPrice: number;
  created: string;
  asks: Array<AskMarketValue>;
  bids: Array<BidMarketValue>;
  spiritoryBottle: boolean;
};
export type SkuPageData = {
  sku: SkuPageDataSku;
  user: {
    id: string | null;
    canBid: boolean | null;
    canAsk: boolean | null;
    emailVerified: boolean | null;
    locale: string | null;
    isAdmin: boolean;
  };
};
